import { Component, Inject, ChangeDetectionStrategy, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { eNormalizedPartyType, eOrderType, ePartyType } from '../../enums';
import { ParsedBusinessReportParty } from '../../interfaces/business-report-parsed-results/parsed-business-report-party';
import { Order } from '../../interfaces/orders/order';
import { AttestationTemplate } from '../../interfaces/attestation-templates/attestation-template';
import { OrderManagerService } from '../../services/order-manager/order-manager.service';
import { OrderRepositoryService } from '../../services/order/order-repository-service.service';
import { SearchResultsRepositoryService } from '../../services/search-results/search-results-repository.service';
import { ConfigurationState } from '../../services/configuration-state/configuration-state';
import { UserGroupRepositoryService } from '../../services/user-group/user-group-repository.service';
import { AttestationTemplateRepositoryService } from '../../services/attestation-templates/attestation-template-repository.service';
import { ParsedBusinessReportEntity } from '../../interfaces/business-report-parsed-results/parsed-business-report-entity';
import { UltimateBeneficialOwnershipRequestForCreation } from '../../interfaces/ultimate-beneficial-ownership-requests/ultimate-beneficial-ownership-request-for-creation';
import { Utilities } from '../../utilities';
import { UboTreeNode } from '../../interfaces/hub-reports/ubo-tree-node';
import { IdentityVerificationDetailsService } from '../../services/identity-verification-details/identity-verification-details.service';
import { IdentityVerificationDetailsForCreation } from '../../interfaces/identity-verification-details/identity-verification-details-for-creation';
import { IdentityVerificationSourceForCreation } from '../../interfaces/identity-verification-source/identity-verification-source-for-creation';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-ultimate-beneficial-ownership-dialog',
  templateUrl: './ultimate-beneficial-ownership-dialog.component.html',
  styleUrl: './ultimate-beneficial-ownership-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UltimateBeneficialOwnershipDialogComponent {
  private readonly transloco = inject(TranslocoService);
  protected entity: ParsedBusinessReportEntity | undefined;
  protected parties: ParsedBusinessReportParty[] = [];
  protected selectedParty: ParsedBusinessReportParty | null = null;
  protected entityName: string = "";
  protected entityNumber: string = "";

  protected templates: AttestationTemplate[] = [];

  private readonly OTHER_PARTY_NAME = this.transloco.translate('ultimateBeneficialOwnershipDialog.otherNotListed');
  private readonly OTHER_PARTY: ParsedBusinessReportParty = {
    name: this.OTHER_PARTY_NAME,
    normalizedPartyType: eNormalizedPartyType.other,
    addresses: []
  };

  selectedTemplate: AttestationTemplate | null = null;

  isSaving: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { node?: UboTreeNode, uboTreeNodeID?: string, entity?: ParsedBusinessReportEntity, previousUboOrder?: Order, businessReportOrder?: Order; },
    private router: Router,
    private orderManager: OrderManagerService,
    private orderRepositoryService: OrderRepositoryService,
    private searchResultsService: SearchResultsRepositoryService,
    private configurationState: ConfigurationState,
    private userGroupService: UserGroupRepositoryService,
    private templatesRepo: AttestationTemplateRepositoryService,
    private identityVerificationDetailsService: IdentityVerificationDetailsService) {
    this.entity = data.entity;
  }

  ngOnInit(): void {
    this.configurationState.getCorporationID().then(corporationID => {
      let params = new HttpParams();
      params = params.set('PageSize', '100');
      params = params.set('corporationId', corporationID);

      this.templatesRepo.getAttestationTemplates(params).subscribe(templates => {
        this.templates = templates;
      });
    });

    if (this.data.entity) {
      this.buildAttestorList(this.data.entity);
    } else if (this.data.businessReportOrder && this.data.businessReportOrder.searchResult?.hasParsedResult) {
      // Technically we already have this in memory in a deeply nested component so we should at some point just get it from there
      // rather than making a request for it
      this.searchResultsService.getBusinessReportParsedResults(this.data.businessReportOrder.id)
        .subscribe(results => this.buildAttestorList(results));
    } else {
      this.buildAttestorList();
    }
  }

  buildAttestorList(entity?: ParsedBusinessReportEntity | null) {
    if (entity) {
      this.entityName = this.entity?.name ?? this.data.node?.party.busName ?? '';
      this.entityNumber = this.entity?.number ?? this.data.node?.party.busNumber ?? '';

      this.entity = entity;
      // filter down to unique, non-business names in list
      const alreadySeen = new Set<string>();
      this.parties = this.entity.parties.filter(party => {
        const normalizedName = party.name?.toUpperCase() ?? "";
        if (alreadySeen.has(normalizedName) || (party.busName?.length ?? 0) > 0) {
          return false;
        }
        alreadySeen.add(normalizedName);
        return true;
      });
    }

    const previousUboAttestor = this.data.previousUboOrder?.parties?.find(p => p.partyTypeID === ePartyType.individualAttestor);

    if (previousUboAttestor) {
      this.parties.unshift({
        name: Utilities.getIndividualName(previousUboAttestor, false),
        firstName: previousUboAttestor.firstName!,
        middleName: previousUboAttestor.middleName!,
        lastName: previousUboAttestor.lastName!,
        normalizedPartyType: eNormalizedPartyType.other,
        contactDetails: {
          email: previousUboAttestor.contactDetails?.email,
          phoneNumber: previousUboAttestor.contactDetails?.phoneNumber
        },
        addresses: []
      });
    }

    this.parties.push(this.OTHER_PARTY);
  }

  canContinue(): boolean {
    return !!this.selectedParty && !!this.selectedTemplate;
  }

  onContinueClick(): void {
    this.isSaving = true;

    this.configurationState.getUserGroupID().then(userGroupID => {
      this.userGroupService.getUserGroup(userGroupID).subscribe(userGroup => {
        const newOrder = {
          referenceNumber: this.data.node?.hubReport?.referenceNumber ??
            this.data.node?.hubReport?.uboTrees.at(0)?.orders.find(o => o.orderTypeID === eOrderType.businessReport)?.referenceNumber ??
            this.data.businessReportOrder?.referenceNumber ?? '',
          jurisdiction: this.data.businessReportOrder?.jurisdiction ?? this.getJurisdictionForUboOrder(this.data.node),
          country: this.data.node?.businessCountry ?? this.data.businessReportOrder?.country ?? this.data.previousUboOrder?.country,
          orderTypeID: eOrderType.ultimateBeneficialOwnershipReport,
          orderGroupID: this.data.businessReportOrder?.orderGroupID ??
            this.data.previousUboOrder?.orderGroupID ??
            this.data.node?.hubReport?.uboTrees.at(0)?.orders.find(o => o.orderTypeID === eOrderType.businessReport)?.orderGroupID,
          uboTreeNodeID: this.data.node?.id ?? this.data.uboTreeNodeID,
          originalOrderID: this.data.businessReportOrder?.id ?? this.data.previousUboOrder?.id,
          ultimateBeneficialOwnershipRequest: {
            templateId: this.selectedTemplate?.id,
            hasDeclaredNoBeneficialOwners: false,
            sendTextInFrench: this.selectedTemplate?.sendTextInFrench ?? false
          } as UltimateBeneficialOwnershipRequestForCreation,
          parties: [
            {
              firstName: this.selectedParty?.firstName ?? null,
              middleName: this.selectedParty?.middleName ?? null,
              lastName: this.selectedParty?.lastName ?? null,
              partyTypeID: ePartyType.individualAttestor,
              contactDetails: {
                email: this.selectedParty?.contactDetails?.email ?? '',
                phoneNumber: this.selectedParty?.contactDetails?.phoneNumber ?? ''
              }
            }
          ]
        };

        this.orderRepositoryService.createNewOrder(newOrder).subscribe(async result => {
          this.isSaving = false;
          const order = (result as any).resource;
          order.ultimateBeneficialOwnershipRequest!.attestor = order.parties?.at(0);
          this.orderManager.updateOrder(order);

          if (order.id) {
            this.router.navigate(['orders', 'business', order.id]);
          }
        });
      });
    });
  }

  private getJurisdictionForUboOrder(node?: UboTreeNode): string {
    if (!node?.businessJurisdiction) {
      return '_M';
    } else {
      return node.businessJurisdiction;
    } 
  }
}